import clsx from 'clsx';
import React from 'react';

import LazyImgixImage from '@components/LazyImgixImage';
import TimeAgo from '@components/TimeAgo';
import useEventTracking from '@hooks/useEventTracking';
import { Theme } from '@interfaces/global';
import { TITVStream } from '@interfaces/homepage';
import { titv_path, titv_show_path } from '@routes';
import { ClickTarget, ContentType, click } from '@tracking';

import Highlight from './Highlight';

interface TITVStreamFeedItemProps {
  titvStream: TITVStream;
  theme?: Theme;
}

const TITVStreamFeedItem = ({ titvStream, theme = Theme.Light }: TITVStreamFeedItemProps) => {
  const { coverUrl, title, guid, datetime: publishedAt } = titvStream;
  const { Track, trackEvent } = useEventTracking({
    contentType: ContentType.TITVStream,
    contentId: guid,
  });

  return (
    <Track>
      <article className="flex flex-col border-0 border-b border-solid border-grayscale-gray px-16 py-32 sm:flex-row sm:px-0 sm:py-48">
        <a
          href={titv_show_path(guid)}
          onClickCapture={() => {
            trackEvent(click({ clickTarget: ClickTarget.TITV, clickText: title }));
          }}
          className="relative -mx-16 block h-[calc(100vw*9/16)] flex-shrink-0 sm:mx-0 sm:h-[calc(213px*9/16)] sm:w-[213px] lg:h-[calc(302px*9/16)] lg:w-[302px]"
        >
          <LazyImgixImage
            className="sm:h-[calc(213px*9/16)] sm:w-[213px] lg:h-[calc(302px*9/16)] lg:w-[302px]"
            src={coverUrl}
            alt={title}
            sizes="(min-width: 1200px) 500px, (min-width: 768px) 300px, (min-width: 480px) 600px, 400px"
            skipOptimization
            wrapperClassName="!flex"
          />
        </a>

        <div className="ml-0 mt-16 sm:ml-24 sm:mt-0">
          <Highlight highlight="TITV" customPath={titv_path()} />
          <h3
            className={clsx('m-0 mb-4 font-suisse-works text-headline-xs lg:text-headline-sm', {
              'text-primary-black': theme === Theme.Light,
              'text-white': theme === Theme.Dark,
            })}
          >
            <a
              href={titv_show_path(guid)}
              onClickCapture={() => {
                trackEvent(click({ clickTarget: ClickTarget.TITVHeadline, clickText: title }));
              }}
              className={clsx('text-balance transition-colors duration-200', {
                'text-primary-black': theme === Theme.Light,
                'text-white': theme === Theme.Dark,
              })}
            >
              <div className="text-primary-black hover:text-primary-magenta dark:hover:text-primary-pink">
                {title}
              </div>
            </a>
          </h3>
          <TimeAgo
            className={clsx('inline-block', {
              'text-grayscale-gray dark:text-grayscale-charcoal': theme === Theme.Light,
              'text-grayscale-beige': theme === Theme.Dark,
            })}
            date={publishedAt}
          />
        </div>
      </article>
    </Track>
  );
};

export default TITVStreamFeedItem;
